import { useState, useCallback } from 'react';
import type { SidebarTabId } from '../types';
import { LEGACY_TAB_FALLBACK } from '../types';

/** Legacy tab IDs that should be auto-docked */
const LEGACY_TAB_IDS = new Set(Object.keys(LEGACY_TAB_FALLBACK));

export interface UseDetachedPanelsReturn {
  detachedPanels: Set<SidebarTabId>;
  detach: (tabId: SidebarTabId) => void;
  dock: (tabId: SidebarTabId) => void;
  isDetached: (tabId: SidebarTabId) => boolean;
}

export function useDetachedPanels(): UseDetachedPanelsReturn {
  const [detachedPanels, setDetachedPanels] = useState<Set<SidebarTabId>>(() => {
    // Cleanup: remove any legacy tab IDs that might be persisted
    return new Set<SidebarTabId>();
  });

  const detach = useCallback((tabId: SidebarTabId) => {
    // Prevent detaching legacy tab IDs
    if (LEGACY_TAB_IDS.has(tabId)) return;
    setDetachedPanels((prev) => {
      if (prev.has(tabId)) return prev;
      const next = new Set(prev);
      next.add(tabId);
      return next;
    });
  }, []);

  const dock = useCallback((tabId: SidebarTabId) => {
    setDetachedPanels((prev) => {
      if (!prev.has(tabId)) return prev;
      const next = new Set(prev);
      next.delete(tabId);
      return next;
    });
  }, []);

  const isDetached = useCallback(
    (tabId: SidebarTabId) => detachedPanels.has(tabId),
    [detachedPanels],
  );

  return { detachedPanels, detach, dock, isDetached };
}
